/* =====================================================================
   VELORA Socke (bewegt)
   The sock picture floats and tilts slightly (mouse, swipe, time). While the
   visitor scrolls through the section, it zooms towards the material points
   of the story steps. The picture itself is never altered – only moved.
   ===================================================================== */
(() => {
  if (window.VeloraSockStory) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = matchMedia('(pointer: fine)').matches;
  const clamp = (x, a = 0, b = 1) => Math.min(b, Math.max(a, x));
  const smooth = (a, b, x) => { const t = clamp((x - a) / (b - a)); return t * t * (3 - 2 * t); };
  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const lerp = (a, b, t) => a + (b - a) * t;
  const damp = (a, b, k, dt) => a + (b - a) * (1 - Math.exp(-k * dt));
  const num = (v, d) => { const n = parseFloat(v); return isFinite(n) ? n : d; };

  class SockStory {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-stage]');
      this.frameEl = root.querySelector('[data-frame]');
      this.pic = root.querySelector('[data-pic]');
      this.tiltEl = root.querySelector('[data-tilt-el]');
      this.intro = root.querySelector('[data-intro]');
      this.cue = root.querySelector('[data-cue]');
      this.dotsEl = root.querySelector('[data-dots]');
      this.sheen = root.querySelector('[data-mask]');
      this.ar = num(this.pic && this.pic.dataset.ar, 0.75);
      this.maxTilt = num(root.dataset.tilt, 14);
      this.stepVH = num(root.dataset.stepVh, 90);
      this.design = root.classList.contains('vss--design');
      this.steps = Array.from(root.querySelectorAll('[data-step]')).map((el, i) => ({
        el,
        fx: clamp(num(el.dataset.x, 50) / 100), fy: clamp(num(el.dataset.y, 50) / 100), z: Math.max(1, num(el.dataset.zoom, 160) / 100),
        align: el.dataset.align === 'right' ? 'right' : 'left',
        pin: this.pic ? this.pic.querySelector(`[data-pin="${i}"]`) : null,
        dot: null,
        pose: null
      }));
      if (this.dotsEl) {
        this.dotsEl.textContent = '';
        this.steps.forEach((st) => { const d = document.createElement('i'); this.dotsEl.appendChild(d); st.dot = d; });
      }
      this.cur = { x: 0, y: 0, s: 1, fx: 0.5, fy: 0.5, z: 1 };
      this.tl = { ry: 0, rx: 0 };
      this.ptr = { x: 0, y: 0, sx: 0, sy: 0 };
      this.touch = null;
      this.time = 0;
      this.last = performance.now();
      this.active = -2;
      this.story = false;
      this.visible = false;
      this.first = true;
      this.ac = new AbortController();
      const sig = { signal: this.ac.signal, passive: true };

      this.measure = this.measure.bind(this);
      addEventListener('resize', this.measure, sig);
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.measure());
        this.ro.observe(this.stage);
        if (this.intro) this.ro.observe(this.intro);
      }
      if (finePointer) {
        addEventListener('pointermove', (e) => {
          if (e.pointerType !== 'mouse') return;
          this.ptr.x = (e.clientX / innerWidth - 0.5) * 2; this.ptr.y = (e.clientY / innerHeight - 0.5) * 2;
        }, sig);
      }
      // touch: a short swipe over the sock turns it a little
      this.stage.addEventListener('touchstart', (e) => { const t = e.touches[0]; if (t) this.touch = { x: t.clientX, d: 0 }; }, sig);
      this.stage.addEventListener('touchmove', (e) => { const t = e.touches[0]; if (t && this.touch) this.touch.d = clamp((t.clientX - this.touch.x) / 120, -1, 1); }, sig);
      const endTouch = () => { this.touch = null; };
      this.stage.addEventListener('touchend', endTouch, sig);
      this.stage.addEventListener('touchcancel', endTouch, sig);
      root.querySelectorAll('[data-go-story]').forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); this.goStep(0, !reduce); }, { signal: this.ac.signal }));

      this.io = new IntersectionObserver(([en]) => {
        this.visible = en.isIntersecting;
        if (!this.visible) return;
        this.last = performance.now();
        // the light reflection is masked to the sock's shape; its image is only requested once the section is near
        if (this.sheen && !this.sheen.classList.contains('has-mask') && this.sheen.dataset.mask) {
          this.sheen.style.setProperty('--mask', `url("${this.sheen.dataset.mask.replace(/"/g, '%22')}")`);
          this.sheen.classList.add('has-mask');
        }
        this.kick();
      }, { rootMargin: '200px' });
      this.io.observe(root);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.measure());
      this.measure();
      root.classList.add('is-ready');
      if (this.design) this.editorHelp();
      this.kick();
    }

    destroy() {
      this.ac.abort();
      if (this.ro) this.ro.disconnect();
      if (this.io) this.io.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0; this.dead = true;
    }

    /* space covered by a sticky header (only if the theme's header is set to sticky) */
    headerReserve() {
      const sh = document.querySelector('sticky-header');
      if (!sh) return 0;
      const type = sh.getAttribute('data-sticky-type');
      if (type !== 'always' && type !== 'reduce-logo-size') return 0;
      return Math.max(0, Math.min(160, sh.offsetHeight || 0));
    }

    measure() {
      if (this.dead) return;
      const r = this.stage.getBoundingClientRect();
      this.W = r.width || innerWidth; this.H = r.height || innerHeight;
      this.mobile = this.W < 900;
      this.top0 = this.headerReserve();
      this.root.style.setProperty('--vss-top', this.top0 + 'px');
      const H = this.H - this.top0;
      this.stepPx = (this.stepVH / 100) * this.H;
      this.span = Math.max(1, this.root.offsetHeight - this.H);
      const maxw = Math.min(this.W, 1240);
      if (!this.mobile) {
        let h = H * 0.84, w = h * this.ar;
        if (w > this.W * 0.42) { w = this.W * 0.42; h = w / this.ar; }
        this.box = { w, h };
        this.introPose = { x: this.W / 2 + maxw / 4, y: this.top0 + H * 0.52, s: 1 };
        const off = Math.min(this.W * 0.22, maxw * 0.26);
        this.steps.forEach((st) => { st.pose = { x: this.W / 2 + (st.align === 'left' ? 1 : -1) * off, y: this.top0 + H * 0.5, s: 1.04 }; });
        this.stage.style.setProperty('--vss-gx', '72%');
      } else {
        let h = H * 0.62, w = h * this.ar;
        if (w > this.W * 0.86) { w = this.W * 0.86; h = w / this.ar; }
        this.box = { w, h };
        let top = this.top0 + H * 0.3, bot = this.H - 52;
        if (this.intro) top = this.intro.offsetTop + this.intro.offsetHeight + 10;
        const avail = Math.max(120, bot - top);
        this.introPose = { x: this.W / 2, y: top + avail / 2, s: clamp(avail / h, 0.35, 1) };
        this.steps.forEach((st) => { st.pose = { x: this.W / 2, y: this.top0 + H * 0.36, s: 0.9 }; });
        this.stage.style.setProperty('--vss-gx', '50%');
      }
      this.frameEl.style.width = this.box.w.toFixed(1) + 'px';
      this.frameEl.style.height = this.box.h.toFixed(1) + 'px';
      this.kick();
    }

    scrollTopFor(i) {
      const top = this.root.getBoundingClientRect().top + scrollY;
      return top + i * this.stepPx + this.stepPx * 0.6;
    }
    goStep(i, smoothScroll) {
      if (!this.steps.length) return;
      scrollTo({ top: this.scrollTopFor(i), behavior: smoothScroll ? 'smooth' : 'auto' });
    }

    kick() { if (!this.raf && !this.dead) this.raf = requestAnimationFrame((t) => this.frame(t)); }

    frame(now) {
      this.raf = 0;
      if (this.dead) return;
      const dt = Math.min(0.05, Math.max(0, (now - this.last) / 1000)); this.last = now; this.time += dt;
      const s = clamp(-this.root.getBoundingClientRect().top, 0, this.span);
      const N = this.steps.length;
      // intro weight: 1 at the start of the section, 0 once the first material step is reached
      const k0 = this.stepPx * 0.55;
      const introW = N ? 1 - smooth(0, k0, s) : 1;
      const copyFade = N ? smooth(0, this.H * 0.3, s) : 0;
      let pose = this.introPose, foc = { x: 0.5, y: 0.5, z: 1 }, stepTilt = 0;
      if (N) {
        const keys = this.steps.map((st, i) => i * this.stepPx + this.stepPx * 0.55);
        let idx = 0;
        while (idx < N - 1 && s > keys[idx + 1]) idx++;
        const a = this.steps[idx], b = this.steps[Math.min(N - 1, idx + 1)];
        let t = 0;
        if (s > keys[idx] && idx < N - 1) t = ease(smooth(0.18, 0.82, (s - keys[idx]) / (keys[idx + 1] - keys[idx])));
        if (reduce) t = t > 0.5 ? 1 : 0;
        const w = reduce ? (introW > 0.5 ? 1 : 0) : ease(introW);
        // zoom towards the step's point and turn the sock slightly towards the caption
        const side = (st) => (st.align === 'left' ? -1 : 1) * this.maxTilt * 0.5;
        foc = { x: lerp(lerp(a.fx, b.fx, t), 0.5, w), y: lerp(lerp(a.fy, b.fy, t), 0.5, w), z: lerp(lerp(a.z, b.z, t), 1, w) };
        stepTilt = lerp(side(a), side(b), t) * (1 - w);
        const sp = { x: lerp(a.pose.x, b.pose.x, t), y: lerp(a.pose.y, b.pose.y, t), s: lerp(a.pose.s, b.pose.s, t) };
        pose = { x: lerp(sp.x, this.introPose.x, w), y: lerp(sp.y, this.introPose.y, w), s: lerp(sp.s, this.introPose.s, w) };
        // active caption
        let act = -1;
        for (let i = 0; i < N; i++) {
          const from = i * this.stepPx + this.stepPx * (i === 0 ? 0.38 : 0.2);
          const to = i === N - 1 ? Infinity : (i + 1) * this.stepPx + this.stepPx * 0.2;
          if (s >= from && s < to) act = i;
        }
        if (act !== this.active) {
          this.steps.forEach((st, i) => {
            st.el.classList.toggle('is-active', i === act);
            if (st.pin) st.pin.classList.toggle('is-active', i === act);
            if (st.dot) st.dot.classList.toggle('is-on', i === act);
          });
          if (this.dotsEl) this.dotsEl.classList.toggle('is-on', act >= 0);
          this.active = act;
        }
        this.stage.style.setProperty('--dim', (0.8 * (1 - w)).toFixed(3));
        const story = introW < 0.5;
        if (story !== this.story) { this.story = story; this.root.classList.toggle('is-story', story); }
      }
      // smoothing (the very first frame jumps straight to the target, so the sock never flies in)
      if (this.first) {
        this.first = false;
        Object.assign(this.cur, { x: pose.x, y: pose.y, s: pose.s, fx: foc.x, fy: foc.y, z: foc.z });
      } else {
        const kk = reduce ? 30 : 7;
        this.cur.x = damp(this.cur.x, pose.x, kk, dt); this.cur.y = damp(this.cur.y, pose.y, kk, dt); this.cur.s = damp(this.cur.s, pose.s, kk, dt);
        this.cur.fx = damp(this.cur.fx, foc.x, kk, dt); this.cur.fy = damp(this.cur.fy, foc.y, kk, dt); this.cur.z = damp(this.cur.z, foc.z, kk, dt);
      }
      this.ptr.sx = damp(this.ptr.sx, this.ptr.x, 2.4, dt); this.ptr.sy = damp(this.ptr.sy, this.ptr.y, 2.4, dt);
      const pw = introW * (finePointer && !reduce && !this.design ? 1 : 0);
      // gentle floating; the focus point of the picture sits on the pose and the picture is scaled around it
      const bob = reduce || this.design ? 0 : Math.sin(this.time * 0.9) * 6 * introW;
      const S = this.cur.s * this.cur.z;
      const left = this.cur.x - this.cur.fx * this.box.w * S, top = this.cur.y + bob - this.cur.fy * this.box.h * S;
      this.frameEl.style.transform = `translate3d(${left.toFixed(1)}px, ${top.toFixed(1)}px, 0) scale(${S.toFixed(4)})`;
      this.frameEl.style.setProperty('--S', S.toFixed(3));
      // slow sway + pointer/swipe at the start, a slight turn towards the caption in the story
      const sway = reduce || this.design ? 0 : Math.sin(this.time * 0.55) * this.maxTilt * 0.55 * introW;
      const swipe = this.touch ? this.touch.d * this.maxTilt * 1.2 : 0;
      const ry = sway + this.ptr.sx * this.maxTilt * pw + stepTilt + swipe, rx = -this.ptr.sy * this.maxTilt * 0.4 * pw;
      const tk = reduce ? 30 : (this.touch ? 10 : 4);
      this.tl.ry = damp(this.tl.ry, ry, tk, dt); this.tl.rx = damp(this.tl.rx, rx, tk, dt);
      if (this.tiltEl) {
        this.tiltEl.style.transform = `perspective(1400px) rotateY(${this.tl.ry.toFixed(2)}deg) rotateX(${this.tl.rx.toFixed(2)}deg)`;
        this.tiltEl.style.setProperty('--sh', clamp(0.5 + this.tl.ry / (2.4 * (this.maxTilt || 14))).toFixed(3));
      }
      // intro text + scroll cue fade out when the story starts
      const op = (1 - copyFade).toFixed(3);
      if (this.intro) { this.intro.style.opacity = op; this.intro.style.visibility = copyFade > 0.99 ? 'hidden' : ''; }
      if (this.cue) this.cue.style.opacity = (1 - Math.min(1, copyFade * 3)).toFixed(3);
      if (this.visible) this.kick();
    }

    /* Theme editor only: click on the sock to get the zoom point in % for a material step. */
    editorHelp() {
      if (this.stage.querySelector('.vss__help')) return;
      const box = document.createElement('div');
      box.className = 'vss__help';
      box.innerHTML = '<strong>Editor-Hilfe</strong><br>Klicke auf eine Stelle der Socke und trage die Werte beim Material-Schritt unter „Zoom-Punkt“ ein.<code data-out>–</code>';
      this.stage.appendChild(box);
      const out = box.querySelector('[data-out]');
      const img = this.pic && this.pic.querySelector('img');
      this.frameEl.addEventListener('click', (e) => {
        if (!img) return;
        const r = img.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width) * 100, y = ((e.clientY - r.top) / r.height) * 100;
        out.textContent = x < 0 || x > 100 || y < 0 || y > 100 ? 'Klicke direkt auf das Bild.' : `Horizontal: ${Math.round(x)} %\nVertikal: ${Math.round(y)} %`;
      }, { signal: this.ac.signal });
    }
  }

  const instances = new Map();
  function init(scope = document) {
    const list = scope.matches && scope.matches('[data-vss]') ? [scope] : Array.from(scope.querySelectorAll('[data-vss]'));
    list.forEach((el) => { if (!instances.has(el)) instances.set(el, new SockStory(el)); });
  }
  function destroy(scope) {
    instances.forEach((inst, el) => { if (scope.contains(el)) { inst.destroy(); instances.delete(el); } });
  }
  window.VeloraSockStory = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  // Theme editor: re-init after re-render, jump to a selected material step
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => destroy(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const step = e.target.closest && e.target.closest('[data-step]');
    const root = e.target.closest && e.target.closest('[data-vss]');
    const inst = root && instances.get(root);
    if (!step || !inst) return;
    const i = inst.steps.findIndex((st) => st.el === step);
    if (i >= 0) inst.goStep(i, false);
  });
})();
